(() => {
  const SELECTOR = '[data-ps-notice-section]';

  class PsNoticeCarousel {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-ps-notice-viewport]');
      this.track = section.querySelector('[data-ps-notice-track]');
      this.realSlides = Array.from(section.querySelectorAll('[data-ps-notice-slide]'));

      this.autoplay = section.dataset.autoplay === 'true';
      this.interval = Math.max(Number(section.dataset.interval) || 5000, 2000);
      this.duration = Math.max(Number(section.dataset.animationDuration) || 500, 0);
      this.pauseOnHover = section.dataset.pauseHover === 'true';
      this.loop = section.dataset.loop === 'true';

      this.index = 0;
      this.timer = null;
      this.slideHeight = 0;
      this.isPaused = false;
      this.isAnimating = false;
      this.resizeTimer = null;
      this.clone = null;
      this.slides = [...this.realSlides];

      if (!this.viewport || !this.track || this.realSlides.length === 0) return;

      this.onResize = this.onResize.bind(this);
      this.onMouseEnter = this.pause.bind(this);
      this.onMouseLeave = this.resume.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onTransitionEnd = this.onTransitionEnd.bind(this);

      this.setup();
    }

    setup() {
      if (this.loop && this.realSlides.length > 1) {
        this.clone = this.realSlides[0].cloneNode(true);
        this.clone.removeAttribute('data-block-id');
        this.clone.removeAttribute('id');
        this.clone.setAttribute('aria-hidden', 'true');
        this.clone.dataset.psNoticeClone = 'true';

        this.clone.querySelectorAll('a, button, input, select, textarea, [tabindex]')
          .forEach((el) => el.setAttribute('tabindex', '-1'));

        this.track.appendChild(this.clone);
        this.slides = [...this.realSlides, this.clone];
      }

      this.track.addEventListener('transitionend', this.onTransitionEnd);
      this.measure();

      if (this.realSlides.length > 1) {
        if (this.pauseOnHover) {
          this.section.addEventListener('mouseenter', this.onMouseEnter);
          this.section.addEventListener('mouseleave', this.onMouseLeave);
        }

        window.addEventListener('resize', this.onResize, { passive: true });
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        if (this.autoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.scheduleNext();
        }
      }
    }

    measure() {
      this.stop();
      this.track.style.transition = 'none';
      this.track.style.transform = 'translate3d(0,0,0)';

      this.slides.forEach((slide) => {
        slide.style.height = 'auto';
      });

      const heights = this.realSlides.map((slide) => Math.ceil(slide.getBoundingClientRect().height));
      this.slideHeight = Math.max(...heights, 1);

      this.slides.forEach((slide) => {
        slide.style.height = `${this.slideHeight}px`;
      });

      this.viewport.style.height = `${this.slideHeight}px`;
      this.track.offsetHeight;

      this.index = Math.min(this.index, this.realSlides.length - 1);
      this.track.style.transition = `transform ${this.duration}ms cubic-bezier(.22,.61,.36,1)`;
      this.goTo(this.index, false);

      if (this.autoplay && !this.isPaused && !document.hidden) {
        this.scheduleNext();
      }
    }

    goTo(index, animate = true) {
      if (this.realSlides.length <= 1) return;

      this.index = index;
      this.isAnimating = animate;

      this.track.style.transitionDuration = animate ? `${this.duration}ms` : '0ms';
      this.track.style.transform = `translate3d(0, -${this.index * this.slideHeight}px, 0)`;
    }

    next() {
      if (this.isAnimating || this.isPaused || document.hidden) return;

      if (this.loop) {
        this.goTo(this.index + 1, true);
        return;
      }

      if (this.index >= this.realSlides.length - 1) {
        this.stop();
        return;
      }

      this.goTo(this.index + 1, true);
    }

    onTransitionEnd(event) {
      if (event.target !== this.track || event.propertyName !== 'transform') return;

      this.isAnimating = false;

      if (this.loop && this.index === this.realSlides.length) {
        this.track.style.transitionDuration = '0ms';
        this.track.style.transform = 'translate3d(0,0,0)';
        this.track.offsetHeight;
        this.index = 0;
        this.track.style.transitionDuration = `${this.duration}ms`;
      }

      this.scheduleNext();
    }

    scheduleNext() {
      this.stop();

      if (
        !this.autoplay ||
        this.realSlides.length <= 1 ||
        this.isPaused ||
        this.isAnimating ||
        document.hidden
      ) return;

      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.next();
      }, this.interval);
    }

    stop() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    pause() {
      this.isPaused = true;
      this.section.classList.add('is-notice-paused');
      this.stop();
    }

    resume() {
      this.isPaused = false;
      this.section.classList.remove('is-notice-paused');
      this.scheduleNext();
    }

    onResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.measure(), 120);
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stop();
      } else {
        this.scheduleNext();
      }
    }

    onBlockSelect(event) {
      if (!this.section.contains(event.target)) return;

      const blockId = event.detail && event.detail.blockId;
      const selectedIndex = this.realSlides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (selectedIndex === -1) return;

      this.pause();
      this.goTo(selectedIndex, true);
    }

    destroy() {
      this.stop();
      window.clearTimeout(this.resizeTimer);

      this.track.removeEventListener('transitionend', this.onTransitionEnd);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.section.removeEventListener('mouseenter', this.onMouseEnter);
      this.section.removeEventListener('mouseleave', this.onMouseLeave);

      if (this.clone && this.clone.parentNode) {
        this.clone.parentNode.removeChild(this.clone);
      }
    }
  }

  const instances = new WeakMap();

  const init = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach((section) => {
      if (instances.has(section)) return;
      instances.set(section, new PsNoticeCarousel(section));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const section =
      event.target.querySelector(SELECTOR) ||
      (event.target.matches?.(SELECTOR) ? event.target : null);

    if (!section) return;

    const instance = instances.get(section);
    if (instance) instance.destroy();
    instances.delete(section);
  });
})();
